import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { useLang, localName } from "../i18n";
import Seo from "../components/Seo";
import { Eyebrow, Reveal, GoldButton } from "../components/motion";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function BecomeAChef() {
  const { t, lang } = useLang();
  const { data: cuisines } = useQuery({ queryKey: ["cuisines"], queryFn: () => api.get("/cuisines").then((r) => r.data) });
  const [form, setForm] = useState({ name: "", email: "", password: "", bio: "", years_experience: "", qualification: "", city: "", cuisine_ids: [] });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const toggleCuisine = (id) =>
    setForm((f) => ({ ...f, cuisine_ids: f.cuisine_ids.includes(id) ? f.cuisine_ids.filter((c) => c !== id) : [...f.cuisine_ids, id] }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/auth/register", { ...form, role: "CHEF", years_experience: Number(form.years_experience) });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="pt-[72px]">
      <Seo title={`${t("becomeChef.title")} — Chef à Domicile`} description={t("becomeChef.sub")} />
      <section className="relative py-24 md:py-32 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1641536618422-2cf0bbd10014?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800" alt="Professional chef" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative max-w-[1100px] mx-auto px-4 sm:px-8">
          <Reveal>
            <Eyebrow>{t("nav.becomeChef")}</Eyebrow>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory mb-5">{t("becomeChef.title")}</h1>
            <p className="text-neutral-300 max-w-2xl">{t("becomeChef.sub")}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-4 sm:px-8 py-16 md:py-24">
        <div className="grid md:grid-cols-3 gap-px bg-white/8 border border-white/8 mb-16">
          {[1, 2, 3].map((n, i) => (
            <Reveal key={n} delay={i * 0.07}>
              <div className="bg-ink p-8 h-full">
                <span className="font-serif text-3xl text-gold/30">0{n}</span>
                <h3 className="font-serif text-xl text-ivory mt-2 mb-2">{t(`becomeChef.s${n}t`)}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{t(`becomeChef.s${n}b`)}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {sent ? (
          <div className="bg-coal border border-gold/30 p-12 text-center" data-testid="chef-apply-success">
            <CheckCircle2 size={40} className="text-gold mx-auto mb-5" />
            <p className="font-serif text-2xl text-ivory">{t("becomeChef.success")}</p>
          </div>
        ) : (
          <Reveal>
            <form onSubmit={submit} className="bg-coal border border-white/8 p-8 md:p-12 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="chef-apply-form">
              <div>
                <label className="lux-label">{t("auth.name")} *</label>
                <input data-testid="chef-name-input" required className="lux-input" value={form.name} onChange={upd("name")} />
              </div>
              <div>
                <label className="lux-label">{t("auth.email")} *</label>
                <input data-testid="chef-email-input" required type="email" className="lux-input" value={form.email} onChange={upd("email")} />
              </div>
              <div>
                <label className="lux-label">{t("auth.password")} *</label>
                <input data-testid="chef-password-input" required type="password" minLength={8} className="lux-input" value={form.password} onChange={upd("password")} />
              </div>
              <div>
                <label className="lux-label">{t("becomeChef.city")} *</label>
                <input data-testid="chef-city-input" required className="lux-input" value={form.city} onChange={upd("city")} />
              </div>
              <div>
                <label className="lux-label">{t("becomeChef.years")} *</label>
                <input data-testid="chef-years-input" required type="number" min="0" className="lux-input" value={form.years_experience} onChange={upd("years_experience")} />
              </div>
              <div>
                <label className="lux-label">{t("becomeChef.qualification")} *</label>
                <input data-testid="chef-qualification-input" required className="lux-input" value={form.qualification} onChange={upd("qualification")} />
              </div>
              <div className="sm:col-span-2">
                <label className="lux-label">{t("becomeChef.bio")} *</label>
                <textarea data-testid="chef-bio-input" required rows={4} className="lux-input" value={form.bio} onChange={upd("bio")} />
              </div>
              <div className="sm:col-span-2">
                <label className="lux-label">{t("becomeChef.cuisines")}</label>
                <div className="flex flex-wrap gap-2">
                  {cuisines?.map((c) => (
                    <button type="button" key={c.id} data-testid={`chef-cuisine-${c.id}`} onClick={() => toggleCuisine(c.id)}
                      className={`text-xs px-4 py-2 border transition-colors ${form.cuisine_ids.includes(c.id) ? "border-gold text-gold bg-gold/10" : "border-white/15 text-neutral-400 hover:border-white/40"}`}>
                      {localName(c, lang)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <GoldButton type="submit" disabled={sending} data-testid="chef-apply-submit-btn" className="w-full">
                  {sending ? t("common.sending") : t("becomeChef.cta")}
                </GoldButton>
              </div>
            </form>
          </Reveal>
        )}
      </section>
    </div>
  );
}
